'use client'

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Facebook, Instagram, Music2, Youtube } from 'lucide-react'

import {
  randomRecentPostIndex,
  shouldUseRibbon,
  type RibbonCapabilities,
} from '@/lib/social-ribbon'
import type { SocialPlatform } from '@/lib/social/dedupe'
import { liveSocialPosts } from '@/lib/social/post-expiry'
import type { createRibbon, RibbonEngine } from '@/components/social-ribbon-gl'

export type RibbonItem = {
  src: string
  href: string
  platform: SocialPlatform
  caption: string
  expiresAt?: string | null
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

const NETWORK = {
  instagram: { label: 'Instagram', Icon: Instagram },
  facebook: { label: 'Facebook', Icon: Facebook },
  youtube: { label: 'YouTube', Icon: Youtube },
  tiktok: { label: 'TikTok', Icon: Music2 },
} as const

function hasWebgl(): boolean {
  const gl = document.createElement('canvas').getContext('webgl')
  gl?.getExtension('WEBGL_lose_context')?.loseContext()
  return Boolean(gl)
}

function readCapabilities(): RibbonCapabilities {
  const nav = navigator as Navigator & {
    deviceMemory?: number
    connection?: { saveData?: boolean }
  }
  return {
    reducedMotion: matchMedia(REDUCED_MOTION).matches,
    webgl: hasWebgl(),
    saveData: Boolean(nav.connection?.saveData),
    deviceMemory: nav.deviceMemory,
    cores: navigator.hardwareConcurrency,
  }
}

const arrowClass =
  'focus-visible:outline-brand-chartreuse-vivid border-brand-lavender/25 hover:border-brand-chartreuse-vivid flex size-11 shrink-0 items-center justify-center rounded-sm border text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-95'

/**
 * Wraps the static list of posts. Without JavaScript, with reduced motion, without
 * WebGL or on a weak device the list is all there is. Otherwise the WebGL ribbon
 * takes over the same box and the list stays in the page, visually hidden, as the
 * crawlable and screen reader version of the same posts.
 */
export function SocialRibbon({
  items: allItems,
  children,
}: {
  items: RibbonItem[]
  children: ReactNode
}) {
  const [now, setNow] = useState(0)
  const items = useMemo(() => liveSocialPosts(allItems, now), [allItems, now])
  const rootRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const createRef = useRef<typeof createRibbon>(null)
  const engineRef = useRef<RibbonEngine>(null)
  const openingHrefRef = useRef<string | null>(null)
  const [mode, setMode] = useState<'static' | 'ribbon'>('static')
  const [active, setActive] = useState(0)
  // Held until the ribbon is ruled out, so a device that keeps the list has no gap under it.
  const [reserve, setReserve] = useState(true)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const expire = () => {
      clearTimeout(timer)
      const time = Date.now()
      setNow(time)
      const next = allItems
        .map((item) => Date.parse(item.expiresAt ?? ''))
        .filter((at) => at > time)
        .sort((a, b) => a - b)[0]
      if (next) timer = setTimeout(expire, Math.min(next - time + 20, 2_147_483_647))
    }
    expire()
    document.addEventListener('visibilitychange', expire)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', expire)
    }
  }, [allItems])

  useEffect(() => {
    rootRef.current?.querySelectorAll<HTMLElement>('[data-social-expires-at]').forEach((row) => {
      row.hidden = !liveSocialPosts([{ expiresAt: row.dataset.socialExpiresAt }], now).length
    })
  }, [now])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const motion = matchMedia(REDUCED_MOTION)
    if (motion.matches) return

    let cancelled = false
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        io.disconnect()
        if (!shouldUseRibbon(readCapabilities(), items.length)) {
          setMode('static')
          setReserve(false)
          return
        }
        void import('@/components/social-ribbon-gl').then(({ createRibbon }) => {
          if (cancelled) return
          createRef.current = createRibbon
          setMode('ribbon')
        })
      },
      { rootMargin: '600px 0px' }
    )
    io.observe(root)
    const onMotion = () => {
      if (motion.matches) {
        setMode('static')
        setReserve(false)
      }
    }
    motion.addEventListener('change', onMotion)
    return () => {
      cancelled = true
      io.disconnect()
      motion.removeEventListener('change', onMotion)
    }
  }, [items.length])

  useEffect(() => {
    const stage = stageRef.current
    const create = createRef.current
    if (mode !== 'ribbon' || !stage || !create) return
    try {
      let initialIndex = items.findIndex((item) => item.href === openingHrefRef.current)
      if (initialIndex < 0) {
        let previous = ''
        try {
          previous = sessionStorage.getItem('rockn-social-opening') ?? ''
        } catch {
          /* Storage is optional. */
        }
        initialIndex = randomRecentPostIndex(items, previous)
        openingHrefRef.current = items[initialIndex]?.href ?? null
        try {
          sessionStorage.setItem('rockn-social-opening', openingHrefRef.current ?? '')
        } catch {
          /* Storage is optional. */
        }
      }
      setActive(initialIndex)
      const engine = create({
        container: stage,
        images: items.map((item) => item.src),
        initialIndex,
        onActive: setActive,
        onOpen: (index) => window.open(items[index]?.href, '_blank', 'noopener,noreferrer'),
        onLost: () => {
          setMode('static')
          setReserve(false)
        },
      })
      engineRef.current = engine
      return () => {
        engine.destroy()
        engineRef.current = null
      }
    } catch {
      queueMicrotask(() => {
        setMode('static')
        setReserve(false)
      })
    }
  }, [mode, items])

  // The visible controls and the panel link reach every post by keyboard, so the
  // hidden list's anchors are taken out of the tab order while the ribbon runs.
  useEffect(() => {
    if (mode !== 'ribbon') return
    const links = Array.from(rootRef.current?.querySelectorAll<HTMLAnchorElement>('ul a') ?? [])
    links.forEach((link) => link.setAttribute('tabindex', '-1'))
    return () => links.forEach((link) => link.removeAttribute('tabindex'))
  }, [mode])

  useEffect(() => {
    const root = rootRef.current
    if (mode !== 'ribbon' || !root) return
    let visible = false
    let hovered = root.matches(':hover')
    let dragging = false
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
      },
      { threshold: 0.5 }
    )
    observer.observe(root)
    const enter = () => {
      hovered = true
    }
    const leave = () => {
      hovered = false
    }
    const down = () => {
      dragging = true
    }
    const up = () => {
      dragging = false
    }
    root.addEventListener('mouseenter', enter)
    root.addEventListener('mouseleave', leave)
    root.addEventListener('pointerdown', down)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    const timer = setInterval(() => {
      if (
        visible &&
        !document.hidden &&
        !hovered &&
        !dragging &&
        !root.contains(document.activeElement)
      ) {
        engineRef.current?.step(1)
      }
    }, 7000)
    return () => {
      clearInterval(timer)
      observer.disconnect()
      root.removeEventListener('mouseenter', enter)
      root.removeEventListener('mouseleave', leave)
      root.removeEventListener('pointerdown', down)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [mode])

  const current = items[active] ?? items[0]
  const network = current ? NETWORK[current.platform] : null

  return (
    // The reserved height is the stage, the gap and the fixed panel, so the ribbon
    // swapping in for the list moves nothing below it.
    <div
      ref={rootRef}
      data-social-ribbon={mode}
      className={
        reserve
          ? 'min-h-[29.5rem] motion-reduce:min-h-0! sm:min-h-[33rem] lg:min-h-[36rem]'
          : undefined
      }
    >
      <div className={mode === 'ribbon' ? 'sr-only' : undefined}>{children}</div>
      {mode === 'ribbon' && current && network && (
        <div>
          <div
            ref={stageRef}
            aria-hidden="true"
            className="h-80 cursor-grab touch-pan-y select-none active:cursor-grabbing sm:h-[23rem] lg:h-[26rem]"
          />
          <div className="mx-auto mt-2 flex h-36 max-w-2xl flex-col gap-2 text-center">
            <p className="font-heading text-brand-chartreuse-vivid inline-flex items-center justify-center gap-2 text-lg tracking-wide uppercase">
              <network.Icon className="size-5" aria-hidden="true" />
              {network.label}
            </p>
            {current.caption && (
              <p className="line-clamp-2 text-base leading-relaxed text-white/80 sm:text-lg">
                {current.caption}
              </p>
            )}
            <div className="mt-auto flex items-center justify-center gap-3">
              <button
                type="button"
                aria-label="Previous post"
                className={arrowClass}
                onClick={() => engineRef.current?.step(-1)}
              >
                <ChevronLeft className="size-6" aria-hidden="true" />
              </button>
              <a
                href={current.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`View on ${network.label} (opens in a new tab)`}
                className="focus-visible:outline-brand-chartreuse-vivid border-brand-chartreuse/30 bg-brand-chartreuse/5 text-brand-chartreuse-vivid hover:border-brand-chartreuse flex size-11 shrink-0 items-center justify-center gap-2 rounded-sm border text-base font-semibold transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 lg:w-auto lg:px-4"
              >
                <network.Icon className="size-5" aria-hidden="true" />
                <span className="hidden lg:inline">View on {network.label}</span>
              </a>
              <button
                type="button"
                aria-label="Next post"
                className={arrowClass}
                onClick={() => engineRef.current?.step(1)}
              >
                <ChevronRight className="size-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
